/**
 * Block 12 PR-H5c Commit 1 — Confirmation dialog для delete сохранённого КП.
 *
 * Mirror'ит `DeleteContractDialog` (Block 13 pattern, PR-G Phase 7 round 7
 * R7-#2) verbatim для Block 12 saved quotes. Overlay 50% bg-black/50,
 * центральная карточка bg-canvas radius 20, role=dialog + aria-modal +
 * aria-labelledby. Initial focus на Cancel (safer default для destructive
 * action). Esc + overlay click = cancel.
 *
 * **Destructive variant**: 2 кнопки (Cancel + Delete), Delete styled через
 * `bg-error` (#dc2626 token из tailwind.config.ts). B-049 «3rd consumer
 * trigger» — это уже 4-й/5-й confirm modal в проекте; refactor в shared
 * `<ConfirmDialog/>` отложен как отдельная задача, эта модалка реализована
 * независимо для scope-tight commit'а (mirror DeleteContractDialog, не shared).
 *
 * a11y: role=dialog, aria-modal, aria-labelledby. Initial focus на Cancel
 * (защита от accidental Enter). Закрытие: overlay click, Escape, кнопка Cancel.
 */

import { useEffect, useRef } from 'react';

export interface DeleteSavedQuoteDialogProps {
  isOpen: boolean;
  quoteName: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DeleteSavedQuoteDialog({
  isOpen,
  quoteName,
  onCancel,
  onConfirm,
}: DeleteSavedQuoteDialogProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  useEffect(() => {
    if (isOpen) cancelButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="delete-saved-quote-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-saved-quote-dialog-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="delete-saved-quote-dialog-title"
          className="font-bold text-ink leading-snug"
          style={{ fontSize: 19, margin: '0 0 24px' }}
          data-testid="delete-saved-quote-dialog-title"
        >
          Удалить КП «{quoteName}»? Это действие нельзя отменить.
        </h2>
        <div className="flex flex-col gap-2.5">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            aria-label="Отмена"
            data-testid="delete-saved-quote-cancel"
            className={
              'w-full font-bold bg-card text-ink border-[1.5px] border-accent ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={onConfirm}
            aria-label="Удалить КП"
            data-testid="delete-saved-quote-confirm"
            className={
              'w-full font-bold bg-error text-white ' +
              'min-h-tap-chip-lg rounded-2xl ' +
              'focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity touch-manipulation'
            }
            style={{ fontSize: 19 }}
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
}
